import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import entete from "../../../assets/img/entete_pool.png";
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';
import axiosInstance from '../../../variables/api/axiosInstance';

const frDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

const DEFAULT_INTRO = "Dans le cadre de l'instruction du dossier susvisé relatif à un accident de la "
    + "circulation qui serait survenu le {date} dans le village de {lieu}, nous soussignés Pool TPVM-VT GIE "
    + "mandatons par la présente Monsieur {investigateur}, investigateur agréé, pour faire une enquête de "
    + "proximité et nous vérifier :";

// Substitue les variables {reference}/{date}/{lieu}/{investigateur} du template ; les valeurs
// substituées sont rendues en gras.
const renderTemplate = (tpl, values) => {
    const parts = (tpl || '').split(/(\{reference\}|\{date\}|\{lieu\}|\{investigateur\})/g);
    return parts.map((part, i) => {
        const key = part.replace(/[{}]/g, '');
        if (part.startsWith('{') && Object.prototype.hasOwnProperty.call(values, key)) {
            return <strong key={i}>{values[key] || '……………'}</strong>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
};

/**
 * MANDAT D'INVESTIGATION — courrier délivré à l'investigateur agréé listant, groupés par
 * rubrique, UNIQUEMENT les points cochés pour ce dossier.
 *
 * `rubriques` : [{ intitule, lieu, demandeLieu, points: ["…", "…"] }] déjà filtré/résolu par
 * le composant appelant (seuls les points cochés, dans l'ordre).
 */
const MandatInvestigation = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    // Responsable (Directeur) récupéré sur l'organisme complet — l'organisme embarqué dans le
    // sinistre n'a pas toujours le champ `responsable` renseigné.
    const [responsableOrganisme, setResponsableOrganisme] = useState(null);
    const afficherEnteteEtPied = props?.afficherEnteteEtPied !== false;

    useEffect(() => {
        const code = props?.sinistre?.contrat?.organisme?.code;
        if (!code) return;
        axiosInstance.get(`/api/organisme/code/${code}`)
            .then(({ data }) => { if (data?.responsable) setResponsableOrganisme(data.responsable); })
            .catch((err) => console.log(err));
    }, [props?.sinistre?.contrat?.organisme?.code]);

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    const blob = new Blob([imageBytes], { type: "image/png" });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((error) => { console.log(error); });
        }
    }, []);

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const sinistre = props?.sinistre;
    // Investigateur : issu de l'expertise (voie experte) OU affecté directement au dossier
    // (voie légère : props.investigateur / sinistre.investigateur).
    const expert = props?.expertise?.expert || props?.investigateur || sinistre?.investigateur;
    const nomInvestigateur = `${expert?.nom || ''} ${expert?.prenom || ''}`.trim() || '……………';
    // Numéro de mandat = numéro de sinistre au format de l'ANCIEN système (numeroSinistreM),
    // repli sur le numéro Pool si absent.
    const reference = props?.reference || sinistre?.numeroSinistreM || sinistre?.numSinistre || '……………';
    const dateAccident = frDate(sinistre?.dateSinistre);
    const lieuAccident = sinistre?.lieu || '……………';
    const rubriques = Array.isArray(props?.rubriques) ? props.rubriques : [];
    // Nom du Directeur/DG = RESPONSABLE de l'organisme lié au contrat du sinistre. On privilégie
    // l'organisme complet fraîchement récupéré, puis celui embarqué, puis la config.
    const directeurNom = (responsableOrganisme || sinistre?.contrat?.organisme?.responsable || props?.directeurNom || '').trim();

    // En-tête d'une rubrique : intitulé + lieu saisi si la rubrique en demande un.
    // Une rubrique SANS titre (ou la rubrique générique « Vérifications générales ») n'affiche
    // AUCUN en-tête : seuls ses points apparaissent.
    const enteteRubrique = (r) => {
        const base = (r?.intitule || '').trim();
        const normalise = base.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        if (!base || normalise === 'verifications generales') {
            return '';
        }
        if (r?.demandeLieu && (r?.lieu || '').trim()) {
            return `${base} ${r.lieu.trim()}`;
        }
        return base;
    };

    return (
        <div ref={ref} style={{
            fontFamily: "Calibri, 'Calibri Light', sans-serif",
            maxWidth: "780px",
            margin: "0 auto",
            padding: afficherEnteteEtPied ? "6px 55px" : "0 55px",
            lineHeight: 1.3,
            fontSize: "12pt",
            color: "#000000",
            display: "flex",
            flexDirection: "column",
            // Sans en-tête (papier pré-imprimé) : on remplit la hauteur imprimable (A4 − marges @page
            // de 20mm = 257mm) et on CENTRE le texte verticalement, pour ne pas empiéter sur l'en-tête
            // ni le pied déjà pré-imprimés.
            minHeight: afficherEnteteEtPied ? "250mm" : "257mm",
            justifyContent: afficherEnteteEtPied ? "flex-start" : "center",
            boxSizing: "border-box"
        }} className="mandat-investigation">
            <style>{`
                .mandat-investigation strong, .mandat-investigation b, .mandat-investigation .mi-titre { font-weight: bold !important; }
                .mandat-investigation p { margin: 0; }
                .mandat-investigation ul { margin: 2px 0 6px 0; padding-left: 26px; }
                .mandat-investigation li { margin-bottom: 2px; }
                @media print { .mandat-investigation { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
            `}</style>

            {afficherEnteteEtPied && (
                <div style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                        {sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && logoPreviewUrl && (
                            <img src={logoPreviewUrl} style={{ width: "auto", height: "55px", objectFit: "contain" }} alt="Logo compagnie" />
                        )}
                        <img src={entete} style={{ width: "auto", height: "55px", objectFit: "contain" }} alt="En-tête" />
                    </div>
                    <div style={{ height: "2px", background: "linear-gradient(to right, #2563eb, #3b82f6)", marginTop: "6px" }} />
                </div>
            )}

            {/* Titre — espace resserré entre le titre et le n° de sinistre */}
            <div style={{ textAlign: "center", marginBottom: "8px", marginTop: "6px" }}>
                <p className="mi-titre" style={{ margin: 0, fontSize: "16pt", fontWeight: "bold", textDecoration: "underline" }}>
                    MANDAT D'INVESTIGATION
                </p>
                <p className="mi-titre" style={{ margin: "1px 0 0 0", fontSize: "12pt", fontWeight: "bold" }}>
                    {reference}{dateAccident ? ` DU ${dateAccident}` : ''}
                </p>
            </div>

            {/* Corps */}
            <div style={{ textAlign: "justify", fontSize: "12pt", lineHeight: 1.3 }}>
                <p style={{ marginBottom: "6px" }}>
                    {renderTemplate(props?.introTemplate || DEFAULT_INTRO, {
                        reference,
                        date: dateAccident || '……………',
                        lieu: lieuAccident,
                        investigateur: nomInvestigateur,
                    })}
                </p>

                {rubriques.map((r, i) => (
                    <div key={i} style={{ marginBottom: "3px" }}>
                        {enteteRubrique(r) && (
                            <p style={{ margin: "2px 0 0 0", fontWeight: "bold" }}>
                                <strong>{enteteRubrique(r)} :</strong>
                            </p>
                        )}
                        <ul>
                            {(r?.points || []).map((pt, j) => (
                                <li key={j}>{pt}</li>
                            ))}
                        </ul>
                    </div>
                ))}

                <p style={{ marginTop: "8px" }}>
                    En foi de quoi, nous lui délivrons le présent mandat d'investigation pour servir
                    et valoir ce que de droit.
                </p>
            </div>

            {/* Signature — comme la prise en charge matériel : NOM du directeur (souligné) en haut,
                puis la mention « Le Directeur » en dessous. Espace de signature au-dessus du nom. */}
            <div style={{ marginTop: "22px", display: "flex", justifyContent: "flex-end" }}>
                <div style={{ textAlign: "left" }}>
                    <p style={{ margin: "0 0 40px 0", fontSize: "12pt" }}>Fait à Lomé, le {today}</p>
                    {directeurNom && (
                        <p style={{ margin: 0, fontSize: "12pt", textDecoration: "underline" }}><strong>{directeurNom}</strong></p>
                    )}
                    <p style={{ margin: "2px 0 0 0", fontSize: "12pt" }}><strong>Le Directeur</strong></p>
                </div>
            </div>

            {afficherEnteteEtPied && <div style={{ flex: 1 }} />}
            {afficherEnteteEtPied && <DocumentFooter />}
        </div>
    );
});

MandatInvestigation.propTypes = {
    expertise: PropTypes.object,
    investigateur: PropTypes.object,
    sinistre: PropTypes.object,
    rubriques: PropTypes.array,
    reference: PropTypes.string,
    introTemplate: PropTypes.string,
    directeurNom: PropTypes.string,
    afficherEnteteEtPied: PropTypes.bool,
};

export default MandatInvestigation;
